import { PaginatedClientAccounts } from '@lune-climate/lune'
import { useQuery } from 'react-query'

import { useLuneClient } from 'hooks/useLuneClient'
import { queryKeys } from 'queryKeys'

/**
 * Loads client accounts with cursor-based pagination
 * @param searchKey - Optional search filter for account names
 * @param cursor - Optional cursor for pagination (use undefined for first page)
 * @param enabled - Whether the query should run
 * @param limit - Number of results per page (default: 10)
 */
const useClientAccounts = (
    searchKey?: string,
    cursor?: string,
    enabled: boolean = true,
    limit: number = 10,
): {
    paginatedAccounts: PaginatedClientAccounts | undefined
    loading: boolean
    refetch: () => void
    hasNextPage: boolean
} => {
    const luneClient = useLuneClient()
    const {
        data: paginatedAccounts,
        isLoading,
        isFetching,
        refetch,
    } = useQuery<PaginatedClientAccounts>(
        [queryKeys.GET_CLIENT_ACCOUNTS, searchKey, cursor, limit],
        async () => {
            const result = await luneClient.listClientAccounts({
                after: cursor,
                name: searchKey,
                limit: limit.toString(),
            })
            return result.unwrap()
        },
        {
            enabled,
            refetchOnMount: true,
        },
    )

    return {
        loading: isLoading || isFetching,
        paginatedAccounts,
        refetch,
        hasNextPage: Boolean(paginatedAccounts?.hasMore && paginatedAccounts.nextCursor),
    }
}

export default useClientAccounts
